import React, { useCallback, useContext, useEffect, useState } from "react";
import { extent } from "./assets/d3";
import { mjd2unix, unix2mjd } from "./helpers/timeParser";
import { useAnimationFrame } from "./helpers/useAnimationFrame";
const GraphTimeContext = React.createContext({
    graphTime: 0,
    updateTime: () => { },
    forward: () => { },
    backward: () => { },
    pause: () => { },
    changeSpeed: () => { },
    goLive: () => { },
    min: 0,
    max: 10,
    speed: 1,
    isPaused: false,
    canUseLive: false,
});
export const GraphTimeProvider = ({ times, children, }) => {
    const [graphTime, setGraphTime] = useState(() => getInitialGraphTime(times));
    const [extents, setExtents] = useState(() => extent(times).map((value) => mjd2unix(value)));
    const [speed, setSpeed] = useState(1);
    const [isPaused, setIsPaused] = useState(false);
    const canUseLive = getIsBetweenExtents(extents);
    const updateGraphTime = useCallback(({ speed, fps }) => {
        setGraphTime((prev) => {
            const nextPossibleValue = prev + (1000 * speed) / fps;
            if (nextPossibleValue < extents[0]) {
                return extents[0];
            }
            else if (nextPossibleValue > extents[1]) {
                return extents[1];
            }
            return nextPossibleValue;
        });
    }, [extents]);
    useAnimationFrame({
        isPaused,
        callback: updateGraphTime,
        speed,
    });
    const changeSpeed = useCallback((newSpeed) => {
        setSpeed(newSpeed);
    }, []);
    const updateTime = useCallback((newValue) => {
        setGraphTime(newValue);
    }, []);
    const pause = useCallback(() => {
        setIsPaused(true);
    }, []);
    const forward = useCallback(() => {
        setIsPaused(false);
        setSpeed((prevSpeed) => Math.abs(prevSpeed));
    }, []);
    const backward = useCallback(() => {
        setIsPaused(false);
        setSpeed((prevSpeed) => -1 * Math.abs(prevSpeed));
    }, []);
    const goLive = useCallback(() => {
        setSpeed(1);
        setGraphTime(new Date().getTime());
    }, []);
    useEffect(() => {
        const newExtents = extent(times).map((value) => mjd2unix(value));
        if (newExtents[0] !== extents[0] && newExtents[1] !== extents[1]) {
            setExtents(newExtents);
        }
    }, [times]);
    if (times.length === 0) {
        return React.createElement(React.Fragment, null, children);
    }
    return (React.createElement(GraphTimeContext.Provider, { value: {
            graphTime,
            updateTime,
            speed,
            min: extents[0] || 0,
            max: extents[1] || 1,
            forward,
            backward,
            pause,
            changeSpeed,
            isPaused,
            canUseLive,
            goLive,
        } }, children));
};
export const useGraphTime = () => useContext(GraphTimeContext);
const getInitialGraphTime = (times) => {
    const extents = extent(times);
    const currentTime = unix2mjd(new Date().getTime());
    if (getIsBetweenExtents(extents, currentTime)) {
        return mjd2unix(currentTime);
    }
    return mjd2unix(extents[0]);
};
const getIsBetweenExtents = (extents, currentTime = new Date().getTime()) => {
    return extents[0] < currentTime && extents[1] > currentTime;
};
//# sourceMappingURL=GraphTimeProvider.js.map